<template>
  <div class="pentagon-candle" :class="[`pentagon-candle--${side}`, `pentagon-candle--${phase}`]">
    <div class="pentagon-candle__glow"></div>

    <div class="pentagon-candle__smoke">
      <span
        v-for="n in 5"
        :key="n"
        class="pentagon-candle__puff"
        :class="`pentagon-candle__puff--${n}`"
      ></span>
      <span class="pentagon-candle__wisp"></span>
    </div>

    <svg class="pentagon-candle__flame" viewBox="0 0 40 72" focusable="false">
      <defs>
        <linearGradient :id="`${uid}-outer`" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stop-color="#a23435" stop-opacity="0.95" />
          <stop offset="0.45" stop-color="#e8501c" />
          <stop offset="1" stop-color="#ff9d1e" />
        </linearGradient>
        <linearGradient :id="`${uid}-body`" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stop-color="#fff1a8" />
          <stop offset="0.5" stop-color="#ffd700" />
          <stop offset="1" stop-color="#ffab1a" />
        </linearGradient>
        <linearGradient :id="`${uid}-core`" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stop-color="#ffffff" />
          <stop offset="1" stop-color="#fff3b8" />
        </linearGradient>
      </defs>
      <g>
        <path
          class="pentagon-candle__layer pentagon-candle__layer--outer"
          :fill="`url(#${uid}-outer)`"
          d="M20 1 C22 14 33 26 33 46 C33 61 27.5 70 20 70 C12.5 70 7 61 7 46 C7 26 18 14 20 1 Z"
        />
      </g>
      <g transform="translate(20 70) scale(0.74) translate(-20 -70)">
        <path
          class="pentagon-candle__layer pentagon-candle__layer--body"
          :fill="`url(#${uid}-body)`"
          d="M20 1 C22 14 33 26 33 46 C33 61 27.5 70 20 70 C12.5 70 7 61 7 46 C7 26 18 14 20 1 Z"
        />
      </g>
      <g transform="translate(20 70) scale(0.4) translate(-20 -70)">
        <path
          class="pentagon-candle__layer pentagon-candle__layer--core"
          :fill="`url(#${uid}-core)`"
          d="M20 1 C22 14 33 26 33 46 C33 61 27.5 70 20 70 C12.5 70 7 61 7 46 C7 26 18 14 20 1 Z"
        />
      </g>
    </svg>

    <span class="pentagon-candle__spark"></span>
    <span class="pentagon-candle__wick"></span>

    <div class="pentagon-candle__wax">
      <svg
        class="pentagon-candle__drips"
        viewBox="0 0 20 90"
        preserveAspectRatio="none"
        focusable="false"
      >
        <g fill="rgba(60, 40, 10, 0.28)">
          <rect x="2.2" y="0" width="3.2" height="30" rx="1.6" />
          <rect x="14.7" y="0" width="3" height="52" rx="1.5" />
          <rect x="8.7" y="0" width="2.4" height="18" rx="1.2" />
        </g>
        <g fill="#fffaf0">
          <rect x="1.5" y="0" width="3.2" height="30" rx="1.6" />
          <rect x="14" y="0" width="3" height="52" rx="1.5" />
          <rect x="8" y="0" width="2.4" height="18" rx="1.2" />
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Candle',
  props: {
    side: {
      type: String,
      default: 'left',
      validator: value => ['left', 'right'].indexOf(value) !== -1,
    },
    phase: {
      type: String,
      default: 'out',
    },
    uid: {
      type: String,
      required: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.pentagon-candle {
  --pentagon-lean: -1;
  --pentagon-delay: 0s;
  --pentagon-tempo: 1;
  --pentagon-offset: 0s;

  position: absolute;
  top: var(--pentagon-candle-top, 18vh);
  bottom: 0;
  width: 1em;
  font-size: var(--pentagon-candle-width, 20px);
  pointer-events: none;

  &--left {
    left: var(--pentagon-candle-edge, 12px);
  }

  &--right {
    --pentagon-lean: 1;
    --pentagon-delay: 0.14s;
    --pentagon-tempo: 1.18;
    --pentagon-offset: -1.1s;

    right: var(--pentagon-candle-edge, 12px);
  }
}

// Wax body: darker on the outer side, with a warm pool below the flame.
.pentagon-candle__wax {
  position: absolute;
  top: 3.4em;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  border-radius: 0.22em 0.22em 0 0;
  background: linear-gradient(
    90deg,
    #6f6652 0%, #b9af97 18%, #efe8d6 45%, #f6f1e6 62%, #d8cfb9 100%);
  box-shadow: inset 0 0.6em 0.5em -0.4em rgba(0, 0, 0, 0.35);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 1.4em;
    background: radial-gradient(
      ellipse at 50% 0,
      rgba(255, 214, 120, 0.75), rgba(255, 214, 120, 0) 70%);
    opacity: 0;
    transition: opacity 0.6s ease-out;
  }
}

.pentagon-candle--right .pentagon-candle__wax {
  background: linear-gradient(
    270deg,
    #6f6652 0%, #b9af97 18%, #efe8d6 45%, #f6f1e6 62%, #d8cfb9 100%);
}

.pentagon-candle--right .pentagon-candle__drips {
  transform: scaleX(-1);
}

.pentagon-candle--igniting .pentagon-candle__wax::before,
.pentagon-candle--lit .pentagon-candle__wax::before {
  opacity: 1;
}

.pentagon-candle__drips {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 9em;
}

.pentagon-candle__wick {
  position: absolute;
  top: 3.05em;
  left: 50%;
  width: 0.12em;
  height: 0.5em;
  margin-left: -0.06em;
  border-radius: 0.06em;
  background: #2a1f18;
}

.pentagon-candle__spark {
  position: absolute;
  top: 2.9em;
  left: 50%;
  width: 0.3em;
  height: 0.3em;
  margin-left: -0.15em;
  border-radius: 50%;
  background: #fff6cf;
  box-shadow: 0 0 0.5em 0.2em rgba(255, 220, 120, 0.9);
  opacity: 0;
}

.pentagon-candle__flame {
  position: absolute;
  // Same ratio as the 40x72 viewBox, so the flame foot sits on the wick.
  top: 0.45em;
  left: -0.25em;
  width: 1.5em;
  height: 2.7em;
  overflow: visible;
  opacity: 0;
  transform: scale(0);
  transform-origin: 50% 100%;
  mix-blend-mode: screen;
  will-change: transform, opacity;
}

.pentagon-candle__layer {
  transform-box: fill-box;
  transform-origin: 50% 100%;
  will-change: transform;
}

.pentagon-candle__glow {
  position: absolute;
  top: -11em;
  left: 50%;
  width: 22em;
  height: 26em;
  margin-left: -11em;
  background: radial-gradient(
    closest-side,
    rgba(255, 190, 70, 0.34), rgba(255, 140, 40, 0.12) 45%, rgba(255, 140, 40, 0) 100%);
  mix-blend-mode: screen;
  opacity: 0;
  will-change: transform, opacity;
}

.pentagon-candle__smoke {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

.pentagon-candle__puff,
.pentagon-candle__wisp {
  position: absolute;
  left: 50%;
  border-radius: 50%;
  opacity: 0;
  will-change: transform, opacity;
}

.pentagon-candle__puff {
  --pentagon-sway: 1;

  top: 0;
  width: 1.6em;
  height: 1.6em;
  margin-left: -0.8em;
  background: radial-gradient(circle, rgba(220, 215, 205, 0.4), rgba(220, 215, 205, 0) 70%);
  filter: blur(0.12em);

  &--1 { --pentagon-sway: 1; }
  &--2 { --pentagon-sway: -1; }
  &--3 { --pentagon-sway: 0.6; }
  &--4 { --pentagon-sway: -0.7; }
  &--5 { --pentagon-sway: 1.2; }
}

.pentagon-candle__wisp {
  top: 1.4em;
  width: 1.2em;
  height: 3em;
  margin-left: -0.6em;
  background: linear-gradient(to top, rgba(235, 230, 220, 0.7), rgba(235, 230, 220, 0));
  filter: blur(0.15em);
}

// ---- Lit and igniting ----

.pentagon-candle--igniting .pentagon-candle__flame {
  animation: flame-ignite 700ms cubic-bezier(0.2, 0.8, 0.3, 1) var(--pentagon-delay) both;
}

.pentagon-candle--lit .pentagon-candle__flame {
  opacity: 1;
  transform: none;
}

.pentagon-candle--igniting .pentagon-candle__layer,
.pentagon-candle--lit .pentagon-candle__layer,
.pentagon-candle--extinguishing .pentagon-candle__layer {
  animation-delay: var(--pentagon-offset);
  animation-iteration-count: infinite;
  animation-timing-function: ease-in-out;
}

.pentagon-candle__layer--outer {
  animation-name: flicker-a;
  animation-duration: calc(2.3s * var(--pentagon-tempo));
}

.pentagon-candle__layer--body {
  animation-name: flicker-b;
  animation-duration: calc(1.7s * var(--pentagon-tempo));
}

.pentagon-candle__layer--core {
  animation-name: flicker-c;
  animation-duration: calc(1.3s * var(--pentagon-tempo));
}

.pentagon-candle--out .pentagon-candle__layer {
  animation: none;
}

.pentagon-candle--igniting .pentagon-candle__glow {
  animation: glow-in 700ms ease-out var(--pentagon-delay) both;
}

.pentagon-candle--lit .pentagon-candle__glow {
  opacity: var(--pentagon-glow-opacity, 1);
  animation: glow-pulse calc(3.4s * var(--pentagon-tempo)) ease-in-out infinite alternate;
}

.pentagon-candle--igniting .pentagon-candle__wick {
  animation: wick-glow 700ms ease-out var(--pentagon-delay) both;
}

.pentagon-candle--igniting .pentagon-candle__spark {
  animation: spark 380ms ease-out calc(var(--pentagon-delay) + 120ms) both;
}

.pentagon-candle--igniting .pentagon-candle__puff,
.pentagon-candle--lit .pentagon-candle__puff {
  animation-name: smoke-rise;
  animation-timing-function: ease-out;
  animation-iteration-count: infinite;

  &--1 { animation-duration: 6s; animation-delay: calc(var(--pentagon-delay) + 0.5s); }
  &--2 { animation-duration: 7.5s; animation-delay: calc(var(--pentagon-delay) + 1.8s); }
  &--3 { animation-duration: 5.5s; animation-delay: calc(var(--pentagon-delay) + 3s); }
  &--4 { animation-duration: 8.5s; animation-delay: calc(var(--pentagon-delay) + 1s); }
  &--5 { animation-duration: 6.8s; animation-delay: calc(var(--pentagon-delay) + 4.2s); }
}

// ---- Extinguishing ----

.pentagon-candle--extinguishing .pentagon-candle__flame {
  animation: flame-out 800ms cubic-bezier(0.4, 0, 0.8, 0.5) var(--pentagon-delay) forwards;
}

.pentagon-candle--extinguishing .pentagon-candle__glow {
  animation: glow-out 450ms ease-in var(--pentagon-delay) forwards;
}

.pentagon-candle--extinguishing .pentagon-candle__wisp {
  animation: wisp-rise 900ms ease-out var(--pentagon-delay) forwards;
}

// ---- Keyframes ----

@keyframes flame-ignite {
  0% {
    opacity: 0;
    transform: scale(0);
  }

  30% {
    opacity: 1;
    transform: scale(0.18, 0.12);
  }

  62% {
    transform: scale(1.18, 1.3);
  }

  82% {
    transform: scale(0.94, 0.96);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes flame-out {
  0% {
    opacity: 1;
    transform: none;
  }

  25% {
    opacity: 1;
    transform: translateX(calc(var(--pentagon-lean) * 0.1em))
      skewX(calc(var(--pentagon-lean) * -18deg))
      scale(1.05, 0.9);
  }

  60% {
    opacity: 0.8;
    transform: translateX(calc(var(--pentagon-lean) * 0.25em))
      skewX(calc(var(--pentagon-lean) * -38deg))
      scale(0.6, 0.5);
  }

  100% {
    opacity: 0;
    transform: translateX(calc(var(--pentagon-lean) * 0.4em))
      skewX(calc(var(--pentagon-lean) * -50deg))
      scale(0.08, 0.06);
  }
}

@keyframes flicker-a {
  0%, 100% { transform: scale(1, 1) skewX(0deg); }
  20% { transform: scale(0.96, 1.04) skewX(2deg); }
  45% { transform: scale(1.03, 0.95) skewX(-2.5deg) translateX(0.02em); }
  70% { transform: scale(0.98, 1.06) skewX(1.5deg); }
}

@keyframes flicker-b {
  0%, 100% { transform: scale(1, 1) skewX(0deg); }
  30% { transform: scale(1.04, 0.94) skewX(-3deg); }
  55% { transform: scale(0.95, 1.07) skewX(2.5deg); }
  80% { transform: scale(1.02, 0.97) skewX(-1deg); }
}

@keyframes flicker-c {
  0%, 100% { transform: scale(1, 1) translateX(0); }
  25% { transform: scale(1.08, 0.92) translateX(0.015em); }
  60% { transform: scale(0.94, 1.1) translateX(-0.02em); }
}

@keyframes glow-in {
  0% {
    opacity: 0;
    transform: scale(0.4);
  }

  60% {
    opacity: calc(var(--pentagon-glow-opacity, 1) * 1.15);
  }

  100% {
    opacity: var(--pentagon-glow-opacity, 1);
    transform: scale(1);
  }
}

@keyframes glow-pulse {
  0% {
    opacity: var(--pentagon-glow-opacity, 1);
    transform: scale(1);
  }

  100% {
    opacity: calc(var(--pentagon-glow-opacity, 1) * 0.82);
    transform: scale(0.96);
  }
}

@keyframes glow-out {
  0% {
    opacity: var(--pentagon-glow-opacity, 1);
    transform: scale(1);
  }

  100% {
    opacity: 0;
    transform: scale(0.3);
  }
}

@keyframes wick-glow {
  0% {
    background: #2a1f18;
    box-shadow: none;
  }

  25%, 60% {
    background: #ff7a2a;
    box-shadow: 0 0 0.3em 0.1em rgba(255, 120, 40, 0.8);
  }

  100% {
    background: #2a1f18;
    box-shadow: none;
  }
}

@keyframes spark {
  0% {
    opacity: 0;
    transform: scale(0.2);
  }

  40% {
    opacity: 1;
    transform: scale(1);
  }

  100% {
    opacity: 0;
    transform: translateY(-0.4em) scale(0.1);
  }
}

@keyframes smoke-rise {
  0% {
    opacity: 0;
    transform: translate3d(0, 0, 0) scale(0.4);
  }

  15% {
    opacity: 0.55;
  }

  50% {
    opacity: 0.3;
    transform: translate3d(calc(var(--pentagon-sway) * 0.6em), -3.5em, 0) scale(1.2);
  }

  100% {
    opacity: 0;
    transform: translate3d(calc(var(--pentagon-sway) * -0.4em), -7em, 0) scale(2.2);
  }
}

@keyframes wisp-rise {
  0% {
    opacity: 0;
    transform: translate3d(0, 0, 0) scale(0.6, 0.8);
  }

  25% {
    opacity: 0.85;
  }

  100% {
    opacity: 0;
    transform: translate3d(calc(var(--pentagon-lean) * 0.5em), -6em, 0) scale(2.2, 1.6);
  }
}

// ---- Narrow viewports: lighter variant ----

@media (max-width: 767.98px) {
  .pentagon-candle__puff {
    filter: none;
  }

  .pentagon-candle__puff--4,
  .pentagon-candle__puff--5 {
    display: none;
  }
}

@media (max-width: 359.98px) {
  .pentagon-candle__glow {
    display: none;
  }
}

// ---- Reduced motion: static lit candle ----

@media (prefers-reduced-motion: reduce) {
  .pentagon-candle .pentagon-candle__flame {
    opacity: 1 !important;
    transform: none !important;
    animation: none !important;
  }

  .pentagon-candle .pentagon-candle__layer,
  .pentagon-candle .pentagon-candle__wick,
  .pentagon-candle .pentagon-candle__glow {
    animation: none !important;
  }

  .pentagon-candle .pentagon-candle__glow {
    opacity: var(--pentagon-glow-opacity, 1) !important;
    transform: none !important;
  }

  .pentagon-candle .pentagon-candle__wax::before {
    opacity: 1;
  }

  .pentagon-candle .pentagon-candle__smoke,
  .pentagon-candle .pentagon-candle__spark {
    display: none;
  }
}
</style>
